import { cacheableTypes } from './cacheableTypes.tsx';
import { CacheHitRatioSummary } from './CacheHitRatioSummary.tsx';
import type { CHRReporterSummary, CHRReporterSummaryEntry } from './CHRReporterSummary.types.tsx';
/**
 * Serves as a tool for capturing and analyzing resource loading performance metrics. By distinguishing between preloaded and all resources, it offers insights into optimizing resource utilization and loading strategies, contributing to enhanced web performance.
 */
export class CacheHitRatioReporter {
	preloaded: CacheHitRatioSummary = new CacheHitRatioSummary();

	all: CacheHitRatioSummary = new CacheHitRatioSummary();

	ssrMark: number | null = null;

	constructor(ssrMark: number | null) {
		this.ssrMark = ssrMark;
	}

	add(item: PerformanceResourceTiming): void {
		if (item.encodedBodySize === 0) {
			return;
		}

		if (
			item.initiatorType === 'link' &&
			cacheableTypes.includes(item.initiatorType) &&
			(this.ssrMark === null || item.startTime < this.ssrMark)
		) {
			this.preloaded.add(item);
		}
		this.all.add(item);
	}

	static round(n: number): number {
		return Math.round(n * 10000) / 10000;
	}

	private makePayload(summary: CacheHitRatioSummary): CHRReporterSummaryEntry {
		const bundlesLoadType = {
			mem: CacheHitRatioReporter.round(
				summary.bundles[CacheHitRatioSummary.MEMORY_KEY] / summary.bundlesCount,
			),
			disk: CacheHitRatioReporter.round(
				summary.bundles[CacheHitRatioSummary.DISK_KEY] / summary.bundlesCount,
			),
			net: CacheHitRatioReporter.round(
				summary.bundles[CacheHitRatioSummary.NETWORK_KEY] / summary.bundlesCount,
			),
		};
		const bundlesRatio = CacheHitRatioReporter.round(
			(summary.bundles[CacheHitRatioSummary.MEMORY_KEY] +
				summary.bundles[CacheHitRatioSummary.DISK_KEY]) /
				summary.bundlesCount,
		);
		const sizeLoadType = {
			mem: CacheHitRatioReporter.round(
				summary.size[CacheHitRatioSummary.MEMORY_KEY] / summary.sizeTotal,
			),
			disk: CacheHitRatioReporter.round(
				summary.size[CacheHitRatioSummary.DISK_KEY] / summary.sizeTotal,
			),
			net: CacheHitRatioReporter.round(
				summary.size[CacheHitRatioSummary.NETWORK_KEY] / summary.sizeTotal,
			),
		};
		const sizeRatio = CacheHitRatioReporter.round(
			(summary.size[CacheHitRatioSummary.MEMORY_KEY] +
				summary.size[CacheHitRatioSummary.DISK_KEY]) /
				summary.sizeTotal,
		);
		return {
			bundles: {
				ratio: bundlesRatio,
				count: summary.bundlesCount,
				loadType: bundlesLoadType,
			},
			size: {
				ratio: sizeRatio,
				total: summary.sizeTotal,
				loadType: sizeLoadType,
			},
		};
	}

	get(): CHRReporterSummary | null {
		if (this.all.bundlesCount === 0) {
			return null;
		}
		return {
			preloaded: this.makePayload(this.preloaded),
			all: this.makePayload(this.all),
		};
	}
}
